import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Task, TaskExecution } from '../../types/task';
import { Connection } from '../../types/connection';
import { StatusIndicator } from '../shared/StatusIndicators/StatusIndicator';
import { DataTable, TableColumn } from '../shared/DataTable/DataTable';
import { TaskExecutionMonitor } from './TaskExecutionMonitor';
import { IntegrationGuide } from './IntegrationGuide';
import { AuditLogViewer } from '../audit';
import { tasksApi } from '../../services/api/tasks';
import { fileAuditApi } from '../../services/api/fileAudit';
import { useTaskExecutionHistory } from '../../hooks/useTaskExecutionHistory';
import { FileEvent } from '../../types/fileAudit';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { RoleGuard } from '../RoleGuard';
import { connectionsApi } from '../../services/api/connections';
import { getRelevantConfigEntries, configFieldLabel } from '../../utils/taskConfigDisplay';
// THUB-186: timezone-aware shared formatter.
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';
// WebSocket temporarily disabled for debugging
// import { useWebSocket, WebSocketMessage } from '../../hooks/useWebSocket';
// Version conflict temporarily disabled for debugging - causes ErrorBoundary crash
// import { useVersionConflict } from '../../hooks/useVersionConflict';

export interface TaskDetailsProps {
  task: Task;
  executions?: TaskExecution[];
  loading?: boolean;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
  onToggleStatus?: (taskId: string, enabled: boolean) => void;
  onExecute?: (taskId: string) => void;
  onClose?: () => void;
  /** Callback when task data should be refreshed (e.g., after SNS topic update) */
  onRefresh?: (taskId: string) => void;
  className?: string;
  /** Whether a delete operation is in progress */
  isDeleting?: boolean;
  /** Error message from a failed delete */
  deleteError?: string | null;
  /** Connection data for integration guide context */
  connection?: Connection;
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
  switch (status) {
    case 'enabled':
    case 'completed':
    case 'success': return 'success';
    case 'running':
    case 'pending': return 'info';
    case 'failed':
    case 'error': return 'error';
    case 'cancelled': return 'warning';
    case 'disabled': return 'neutral';
    default: return 'neutral';
  }
};

const formatDate = (date: Date | string): string => {
  // THUB-186: was `new Intl.DateTimeFormat('en-US', {...}).format()` with NO timeZone,
  // which renders in the browser's zone and ignores the user's selection. It also fed a
  // bare `new Date(str)`, so a NAIVE backend timestamp was parsed as local time and shown
  // offset-wrong. 'precise' keeps the previous second-level granularity.
  //
  // Note this used Intl directly rather than toLocaleString, so a grep for
  // `toLocale*` — the one the audit and I both ran first — did not surface it.
  return formatDateTime(date, activeTimezone(), 'precise');
};

const formatDuration = (startTime: Date | string, endTime?: Date | string): string => {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  if (!endTime) return 'Running...';
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  const duration = end.getTime() - start.getTime();
  const seconds = Math.floor(duration / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};


export function TaskDetails({
  task,
  executions = [],
  loading = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onExecute,
  onClose,
  onRefresh,
  className = '',
  isDeleting: isDeletingProp = false,
  deleteError: deleteErrorProp = null,
  connection,
}: TaskDetailsProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'configuration' | 'history' | 'logs' | 'monitor' | 'subscribers' | 'audit'>('overview');
  const [, setShowMonitor] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Local state for SNS topic ARN (can be updated via WebSocket)
  // THUB-298: local OVERRIDES over the task prop, not mirrors of it.
  //
  // These were useState copies of task.snsTopicArn / task.pgpEnabled re-synced by two effects. A
  // mirror plus an effect is strictly worse than a derivation: between the prop changing and the
  // effect running, the render showed the OLD value, and if the prop changed back the effect
  // dutifully overwrote a local optimistic update. Each override now holds only what this
  // component has changed itself, and the displayed value is `override ?? prop`.
  // No override state for the SNS ARN: nothing in live code ever set one. The old
  // `localSnsTopicArn` useState existed only to be re-synced from the prop by an effect, and its
  // only other writers are in the commented-out version-conflict block below. So it is simply the
  // prop, read directly.
  const localSnsTopicArn = task.snsTopicArn;

  // Track if SNS topic creation is in progress
  const [isCreatingSns, setIsCreatingSns] = useState(false);

  // PGP encryption toggle state
  const [pgpEnabledOverride, setPgpEnabledOverride] = useState<boolean | undefined>(undefined);
  const pgpEnabled = pgpEnabledOverride ?? (task.pgpEnabled || false);

  // THUB-183: lazy-loaded SNS subscribers (Subscribers tab)
  type TaskSubscriber = {
    protocol: string;
    endpoint: string;
    ownerAccountId: string;
    subscriptionArn: string;
    status: 'confirmed' | 'pending';
  };
  // THUB-299: both leaf tabs were hand-rolled fetch-in-effect. Each carried three useState
  // values and an effect whose dependency array included the very state it set
  // (`subscribers !== null || subscribersLoading`) to stop itself re-entering -- the pattern
  // react-hooks/set-state-in-effect flags, and a latent stuck-spinner if the guard is ever wrong.
  //
  // `enabled` expresses the laziness directly: nothing is requested until the tab is opened,
  // which is the behaviour worth keeping (Logs pulls up to 50 events).
  //
  // Both keys sit under queryKeys.tasks.detail, so an `execution_updates` broadcast -- which
  // invalidates queryKeys.tasks.all and matches by prefix -- refreshes an open Logs tab as new
  // file events arrive. Previously it could not: the rows lived in local state, invisible to the
  // cache. Invalidations are coalesced upstream by useCoalescedInvalidate, so a burst of
  // file_ready events is one refetch, not one per file.
  const subscribersQuery = useQuery({
    queryKey: [...queryKeys.tasks.detail(task.taskId), 'subscribers'],
    queryFn: () => tasksApi.getTaskSubscribers(task.taskId),
    enabled: activeTab === 'subscribers',
  });
  const subscribers: TaskSubscriber[] | null = subscribersQuery.data?.subscribers ?? null;
  const subscribersLoading = subscribersQuery.isFetching;
  const subscribersError = subscribersQuery.isError ? 'Failed to load subscribers' : null;
  const hasNoTopic = subscribersQuery.data ? !subscribersQuery.data.snsTopicArn : false;

  // THUB-198: the Logs tab renders this task's recent file-journey events (from file_audit_api)
  // instead of a static "Logs are in CloudWatch" stub.
  const logsQuery = useQuery({
    queryKey: [...queryKeys.tasks.detail(task.taskId), 'fileEvents', 50],
    queryFn: () => fileAuditApi.getTaskFileEvents(task.taskId, { limit: 50 }),
    enabled: activeTab === 'logs',
  });
  const logEvents: FileEvent[] | null = logsQuery.data ? (logsQuery.data.events || []) : null;
  const logsLoading = logsQuery.isFetching;
  const logsError = logsQuery.isError ? 'Failed to load file events' : null;

  // THUB-187: task-scoped, cursor-paginated Execution History. Lazily activated —
  // we only pass the taskId to the hook once the History tab has been opened, so
  // the fetch doesn't fire for tasks whose history is never viewed (matches the
  // lazy pattern used by the Logs/Subscribers tabs). Once activated it stays so,
  // preserving loaded pages when switching tabs and back.
  // THUB-298: latched at the point the tab is CHOSEN, not by an effect watching activeTab.
  //
  // The latch itself is deliberate (see above): once History has been opened the fetch stays
  // enabled so loaded pages survive tab switches. But an effect was the wrong place to record it
  // -- it re-derived a fact the tab-change already knew, one render later. `selectTab` below sets
  // both, so activation and the tab change land in the same update.
  const [historyActivated, setHistoryActivated] = useState(activeTab === 'history');

  type TabId = Parameters<typeof setActiveTab>[0] extends infer T ? Extract<T, string> : never;
  /** Change tab, latching History activation in the same update. */
  const selectTab = useCallback((tab: TabId) => {
    setActiveTab(tab);
    if (tab === 'history') setHistoryActivated(true);
  }, []);
  const {
    executions: historyExecutions,
    isLoading: historyLoading,
    isRefreshing: historyRefreshing,
    error: historyError,
    hasMore: historyHasMore,
    hasPendingUpdates: historyHasPendingUpdates,
    loadMore: loadMoreHistory,
    refresh: refreshHistory,
  } = useTaskExecutionHistory(historyActivated ? task.taskId : null);

  // THUB-278: the execution the Monitor tab should display.
  //
  // Both sources are newest-first, so index 0 is the task's latest run. The
  // container-fed `executions` prop is preferred because it is fetched when the task
  // detail opens (all three containers request 50 rows); `historyExecutions` covers
  // the case where only the History tab has fetched. Either way the Monitor now has
  // something to render for a task whose last run has already completed.
  const monitorSeedExecution = executions[0] ?? historyExecutions[0];

  // THUB-278: the execution chosen from the History table, shown in a detail overlay.
  // Undefined = no row selected (overlay closed).
  const [selectedExecution, setSelectedExecution] = useState<TaskExecution | undefined>();

  // Fetch connection data if not provided as prop (needed for integration guide).
  //
  // THUB-299: was a useState mirroring the prop plus an effect that either copied the prop into
  // state or fetched by id. The prop-wins rule is now expressed by DERIVING the value rather than
  // by an effect that copies one source of truth into another -- so there is no window in which
  // state still holds the previous connection while the prop already names a new one.
  //
  // This value decides whether the PGP controls appear and whether remote-only UI is shown, so
  // `enabled` matters: no prop and no connectionId must mean no request, not a request for
  // undefined.
  const connectionQuery = useQuery({
    queryKey: queryKeys.connections.detail(task.connectionId ?? ''),
    queryFn: () => connectionsApi.getConnection(task.connectionId as string),
    enabled: !connection && !!task.connectionId,
  });
  // A failed read is swallowed exactly as before (the original used `.catch(() => {})` with
  // "guide falls back to generic variant") -- it degrades the integration guide, never the modal.
  const fetchedConnection: Connection | undefined = connection ?? connectionQuery.data;
  const [isTogglingPgp, setIsTogglingPgp] = useState(false);

  // WebSocket subscription for real-time updates - temporarily disabled for debugging
  // const { subscribeToEntity, isConnected } = useWebSocket();

  // Version conflict handling - temporarily disabled for debugging
  // const {
  //   hasConflict,
  //   conflictState,
  //   startTracking,
  //   stopTracking,
  //   refresh: refreshVersion,
  //   clearConflict,
  // } = useVersionConflict({
  //   entityType: 'task',
  //   onConflict: (state) => {
  //     console.log('Task version conflict detected:', state);
  //   },
  //   onExternalUpdate: (version) => {
  //     console.log('Task updated externally:', version);
  //   },
  // });

  // Temporary stub values while version conflict is disabled (kept for future re-enablement)

  // State for showing conflict toast - disabled
  // const [showConflictToast, setShowConflictToast] = useState(false);

  // THUB-203: only offer PGP when the connection has the key required for this
  // task's direction (public for outbound, private for inbound). Carve-out: if
  // the task already has PGP enabled, keep the toggle visible so it can be
  // turned off even when the connection key is missing.
  const pgpKeyAvailable = fetchedConnection
    ? (task.transferDirection === 'outbound'
        ? !!fetchedConnection.pgpPublicKeyConfigured
        : !!fetchedConnection.pgpPrivateKeyConfigured)
    : false;
  const showPgpSection = pgpKeyAvailable || pgpEnabled;

  // Handle PGP encryption toggle
  const handleTogglePgp = async (enabled: boolean) => {
    setIsTogglingPgp(true);
    try {
      await tasksApi.updateTask(task.taskId, { pgpEnabled: enabled });
      setPgpEnabledOverride(enabled);
      toast.success(`PGP encryption ${enabled ? 'enabled' : 'disabled'}`);
      onRefresh?.(task.taskId);
    } catch (error: any) {
      if (error?.code === 'PGP_KEY_MISSING') {
        toast.error('PGP key must be configured on the connection before enabling PGP encryption');
      } else {
        toast.error(`Failed to ${enabled ? 'enable' : 'disable'} PGP encryption`);
      }
    } finally {
      setIsTogglingPgp(false);
    }
  };

  // Track task version for conflict detection - temporarily disabled for debugging
  // useEffect(() => {
  //   try {
  //     if (task.taskId && typeof task.version === 'number') {
  //       startTracking(task.taskId, {
  //         version: task.version,
  //         updatedAt: task.updatedAt || new Date().toISOString(),
  //         updatedBy: task.createdBy, // Using createdBy as Task type doesn't have updatedBy
  //       });
  //     }
  //   } catch (error) {
  //     console.warn('Failed to start version tracking for task:', error);
  //   }
  //   return () => {
  //     try {
  //       stopTracking();
  //     } catch (error) {
  //       console.warn('Failed to stop version tracking:', error);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [task.taskId, task.version]);

  // Show conflict toast when conflict detected - temporarily disabled
  // useEffect(() => {
  //   if (hasConflict) {
  //     setShowConflictToast(true);
  //   }
  // }, [hasConflict]);

  // Handle SNS topic update from WebSocket - temporarily disabled for debugging
  // const handleSnsTopicUpdate = useCallback((message: WebSocketMessage<SnsTopicUpdatedPayload>) => {
  //   const payload = message.payload;
  //   if (payload.taskId !== task.taskId) { return; }
  //   if (payload.status === 'created' || payload.status === 'updated') {
  //     setLocalSnsTopicArn(payload.snsTopicArn);
  //     setIsCreatingSns(false);
  //     onRefresh?.(task.taskId);
  //   } else if (payload.status === 'deleted') {
  //     setLocalSnsTopicArn(undefined);
  //     setIsCreatingSns(false);
  //   }
  // }, [task.taskId, onRefresh]);

  // Subscribe to task entity updates for SNS topic changes - temporarily disabled for debugging
  // useEffect(() => {
  //   if (!isConnected) {
  //     return;
  //   }
  //
  //   // Subscribe to entity changes for this specific task
  //   const unsubscribe = subscribeToEntity<SnsTopicUpdatedPayload>(
  //     'task',
  //     task.taskId,
  //     handleSnsTopicUpdate
  //   );
  //
  //   return unsubscribe;
  // }, [task.taskId, subscribeToEntity, isConnected, handleSnsTopicUpdate]);

  // Track SNS topic creation when task is enabled without an existing topic
  const handleToggleWithSnsTracking = (taskId: string, enabled: boolean) => {
    // If enabling the task and no SNS topic exists yet, track creation state
    if (enabled && !localSnsTopicArn) {
      setIsCreatingSns(true);
    }
    onToggleStatus?.(taskId, enabled);
  };

  // Handle version conflict refresh - temporarily disabled for debugging
  // const handleConflictRefresh = useCallback(async () => {
  //   setShowConflictToast(false);
  //   clearConflict();
  //   onRefresh?.(task.taskId);
  // }, [clearConflict, onRefresh, task.taskId]);

  const handleAction = (action: 'edit' | 'delete' | 'toggle' | 'execute') => {
    switch (action) {
      case 'edit':
        onEdit?.(task);
        break;
      case 'delete':
        setShowDeleteConfirm(true);
        break;
      case 'toggle':
        // Use the SNS tracking handler instead of direct call
        handleToggleWithSnsTracking(task.taskId, task.status === 'disabled');
        break;
      case 'execute':
        onExecute?.(task.taskId);
        setShowMonitor(true);
        selectTab('monitor');
        break;
    }
  };

  // THUB-143: Execute Now only applies to RUNNABLE shapes — there must be a
  // schedule/pickup to run ahead of. Show it only for:
  //  - remote-inbound pickup (SFTP or HTTP), and
  //  - remote-outbound + deliveryMode 'scheduled'.
  // Hide it for event-driven shapes (hosted inbound/outbound, remote-outbound
  // immediate, HTTP outbound) where files flow automatically on drop/receipt.
  // The backend also returns 409 EXECUTE_NOT_APPLICABLE for these as a guard.
  const isRemoteConn =
    fetchedConnection?.hostingType === 'remote' || fetchedConnection?.transferMethod === 'api';
  const canExecuteNow =
    (isRemoteConn && task.transferDirection === 'inbound') ||
    (fetchedConnection?.hostingType === 'remote' &&
      fetchedConnection?.transferMethod !== 'api' &&
      task.transferDirection === 'outbound' &&
      task.deliveryMode === 'scheduled');

  const tabs: { id: typeof activeTab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'configuration', label: 'Configuration' },
    // THUB-187: once the paginated hook is active, its loaded-row count is the
    // truthier number; before activation, use the container-fed seed count.
    { id: 'history', label: 'History', count: historyActivated ? historyExecutions.length : executions.length },
    { id: 'logs', label: 'Logs' },
    { id: 'monitor', label: 'Monitor' },
    { id: 'subscribers', label: 'Subscribers' },
    { id: 'audit', label: 'Audit History' },
  ];

  const executionColumns: TableColumn<TaskExecution>[] = [
    {
      key: 'startTime',
      title: 'Start Time',
      sortable: true,
      render: (value) => formatDate(new Date(value)),
      width: '150px'
    },
    {
      // THUB-204: hosted-inbound writes one row per file — show which file each
      // row is for. Falls back to a dash for legacy/batch rows with no filename.
      key: 'filename',
      title: 'File',
      render: (value) => value ? (
        <span
          className="text-neutral-800 dark:text-slate-200 text-xs break-all"
          title={value}
        >
          {value.length > 40 ? `${value.substring(0, 40)}...` : value}
        </span>
      ) : (
        <span className="text-neutral-400 dark:text-slate-500">—</span>
      ),
      width: '200px'
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => (
        <StatusIndicator
          status={getStatusColor(value)}
          label={value.charAt(0).toUpperCase() + value.slice(1)}
          size="small"
        />
      ),
      width: '100px'
    },
    {
      key: 'metrics',
      title: 'Files',
      render: (value) => value?.filesProcessed?.toLocaleString() ?? '0',
      width: '80px',
      align: 'right'
    },
    {
      key: 'metrics',
      title: 'Data',
      render: (value) => formatBytes(value?.totalFileSize ?? 0),
      width: '100px',
      align: 'right'
    },
    {
      key: 'startTime',
      title: 'Duration',
      render: (_, execution) => formatDuration(execution.startTime, execution.endTime),
      width: '100px'
    },
    {
      key: 'error',
      title: 'Error',
      render: (value) => value?.errorMessage ? (
        <span
          className="text-danger-500 text-xs"
          title={value.errorMessage}
        >
          {value.errorMessage.length > 50 ? `${value.errorMessage.substring(0, 50)}...` : value.errorMessage}
        </span>
      ) : null,
      width: '200px'
    }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="
        bg-neutral-50 dark:bg-slate-800/50
        rounded-lg p-5
        border border-neutral-200 dark:border-slate-700
      ">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          mb-4
        ">
          Basic Information
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Task Name
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {task.name}
            </span>
          </div>
          {/* THUB-160: display label (optional) shown alongside the immutable name */}
          {task.description && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Display Name
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {task.description}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Partner
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {task.partnerName}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Transfer Type
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {task.transferDirection}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Status
            </label>
            <StatusIndicator
              status={getStatusColor(task.status)}
              label={task.status.charAt(0).toUpperCase() + task.status.slice(1)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Created
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {formatDate(task.createdAt)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Last Updated
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {formatDate(task.updatedAt)}
            </span>
          </div>
        </div>
      </div>

      {/* PGP Encryption - THUB-203: hidden unless the connection has the key
          matching this task's direction (or PGP is already enabled). */}
      {showPgpSection && (
      <div className="
        bg-neutral-50 dark:bg-slate-800/50
        rounded-lg p-5
        border border-neutral-200 dark:border-slate-700
      ">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          mb-4
        ">
          PGP Encryption
        </h4>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {pgpEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-slate-400">
              {task.transferDirection === 'outbound'
                ? 'Files will be PGP-encrypted before delivery'
                : 'Incoming files will be PGP-decrypted after receipt'}
            </span>
          </div>
          <button
            onClick={() => handleTogglePgp(!pgpEnabled)}
            disabled={isTogglingPgp}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full
              transition-colors duration-200 ease-in-out
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              ${pgpEnabled
                ? 'bg-success-500'
                : 'bg-neutral-300 dark:bg-slate-600'
              }
            `}
            role="switch"
            aria-checked={pgpEnabled}
            aria-label="Toggle PGP encryption"
          >
            <span
              className={`
                inline-block h-4 w-4 rounded-full bg-white
                transition-transform duration-200 ease-in-out
                shadow-sm
                ${pgpEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>
      )}

      {/* Developer Integration Guide */}
      <IntegrationGuide
        task={task}
        snsTopicArn={localSnsTopicArn}
        isCreatingSns={isCreatingSns}
        connection={fetchedConnection}
      />

      {/* Schedule — outbound scheduled delivery (THUB-142) takes precedence over legacy schedule object */}
      {task.transferDirection === 'outbound' && task.deliveryMode ? (
        <div className="
          bg-neutral-50 dark:bg-slate-800/50
          rounded-lg p-5
          border border-neutral-200 dark:border-slate-700
        ">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            mb-4
          ">
            Schedule
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Delivery Mode
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {task.deliveryMode === 'scheduled' ? 'Scheduled' : 'Immediate'}
              </span>
            </div>
            {task.deliveryMode === 'scheduled' && task.deliverySchedule && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                  Expression
                </label>
                <span className="
                  inline-block w-fit px-2 py-1 rounded text-xs font-mono
                  bg-white dark:bg-slate-900
                  border border-neutral-200 dark:border-slate-700
                  text-neutral-800 dark:text-slate-200
                ">
                  {task.deliverySchedule}
                </span>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Enabled
              </label>
              <StatusIndicator
                status={task.status === 'enabled' ? 'success' : 'neutral'}
                label={task.status === 'enabled' ? 'Yes' : 'No'}
                size="small"
              />
            </div>
          </div>
        </div>
      ) : task.schedule && (
        <div className="
          bg-neutral-50 dark:bg-slate-800/50
          rounded-lg p-5
          border border-neutral-200 dark:border-slate-700
        ">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            mb-4
          ">
            Schedule
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Type
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {task.schedule.type.charAt(0).toUpperCase() + task.schedule.type.slice(1)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Expression
              </label>
              <span className="
                inline-block w-fit px-2 py-1 rounded text-xs font-mono
                bg-white dark:bg-slate-900
                border border-neutral-200 dark:border-slate-700
                text-neutral-800 dark:text-slate-200
              ">
                {task.schedule.expression}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Timezone
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {task.schedule.timezone}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                Enabled
              </label>
              <StatusIndicator
                status={task.schedule.enabled ? 'success' : 'neutral'}
                label={task.schedule.enabled ? 'Yes' : 'No'}
                size="small"
              />
            </div>
            {task.schedule.nextRun && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                  Next Run
                </label>
                <span className="text-sm text-neutral-900 dark:text-slate-100">
                  {formatDate(task.schedule.nextRun)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      <div className="
        bg-neutral-50 dark:bg-slate-800/50
        rounded-lg p-5
        border border-neutral-200 dark:border-slate-700
      ">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          mb-4
        ">
          Performance Metrics
        </h4>
        {task.metrics.totalExecutions === 0 && task.metrics.totalFilesProcessed === 0 ? (
          <div className="flex items-center justify-center py-8 text-neutral-500 dark:text-slate-400">
            <p className="text-sm">No execution data yet</p>
          </div>
        ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="
            bg-white dark:bg-slate-900
            p-4 rounded-lg text-center
            border border-neutral-200 dark:border-slate-700
          ">
            <div className="text-2xl font-bold text-neutral-800 dark:text-slate-200 mb-1">
              {task.metrics.totalExecutions}
            </div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Total Runs
            </div>
          </div>
          <div className="
            bg-success-50 dark:bg-success-900/20
            p-4 rounded-lg text-center
            border border-success-200 dark:border-success-800
          ">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400 mb-1">
              {task.metrics.successfulExecutions}
            </div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Successful
            </div>
          </div>
          <div className="
            bg-danger-50 dark:bg-danger-900/20
            p-4 rounded-lg text-center
            border border-danger-200 dark:border-danger-800
          ">
            <div className="text-2xl font-bold text-danger-600 dark:text-danger-400 mb-1">
              {task.metrics.failedExecutions}
            </div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Failed
            </div>
          </div>
          <div className="
            bg-white dark:bg-slate-900
            p-4 rounded-lg text-center
            border border-neutral-200 dark:border-slate-700
          ">
            <div className="text-2xl font-bold text-neutral-800 dark:text-slate-200 mb-1">
              {task.metrics.totalExecutions > 0
                ? Math.round((task.metrics.successfulExecutions / task.metrics.totalExecutions) * 100)
                : 0}%
            </div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Success Rate
            </div>
          </div>
          <div className="
            bg-white dark:bg-slate-900
            p-4 rounded-lg text-center
            border border-neutral-200 dark:border-slate-700
          ">
            <div className="text-2xl font-bold text-neutral-800 dark:text-slate-200 mb-1">
              {task.metrics.totalFilesProcessed.toLocaleString()}
            </div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Files Processed
            </div>
          </div>
          <div className="
            bg-white dark:bg-slate-900
            p-4 rounded-lg text-center
            border border-neutral-200 dark:border-slate-700
          ">
            <div className="text-2xl font-bold text-neutral-800 dark:text-slate-200 mb-1">
              {formatBytes(task.metrics.totalBytesTransferred)}
            </div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
              Data Transferred
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );

  const renderConfiguration = () => {
    // THUB-163: single source of truth for which config fields are relevant.
    // fetchedConnection gives us hostingType so we can also hide remote-only
    // path fields for hosted tasks (previously only transferDirection was used).
    // THUB-223: postPickupAction / postPickupTargetPath live on the TASK, not inside
    // task.configuration — the API writes the top-level fields and the nested copy is
    // never populated. Reading only task.configuration meant this tab showed
    // "DELETE AFTER TRANSFER: No" for a task whose stored postPickupAction is literally
    // "delete", which is worse than omitting it: it actively asserts the opposite of the
    // truth. Merge them in so the tab reflects what the task will really do.
    //
    // Only merged when set, so getRelevantConfigEntries' empty-value filter still hides
    // them for tasks with no post-pickup action ("none" is the unset sentinel).
    const configWithWorkflowFields: Record<string, unknown> = {
      ...(task.configuration as Record<string, unknown> | undefined),
    };
    if (task.postPickupAction && task.postPickupAction !== 'none') {
      configWithWorkflowFields.postPickupAction = task.postPickupAction;
      if (task.postPickupTargetPath) {
        configWithWorkflowFields.postPickupTargetPath = task.postPickupTargetPath;
      }
    }

    const entries = getRelevantConfigEntries(
      configWithWorkflowFields,
      fetchedConnection?.hostingType,
      task.transferDirection
    );

    return (
      <div className="space-y-6">
        <div className="
          bg-neutral-50 dark:bg-slate-800/50
          rounded-lg p-5
          border border-neutral-200 dark:border-slate-700
        ">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            mb-4
          ">
            Transfer Configuration
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {entries.map(([key, value]) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
                  {configFieldLabel(key)}
                </label>
                <span className="
                  text-sm text-neutral-900 dark:text-slate-100
                  bg-white dark:bg-slate-900
                  px-3 py-2 rounded
                  border border-neutral-200 dark:border-slate-700
                ">
                  {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                </span>
              </div>
            ))}
          </div>
          {entries.length === 0 && (
            <p className="text-sm text-neutral-500 dark:text-slate-400 italic">No configuration settings</p>
          )}
        </div>
      </div>
    );
  };

  // THUB-187: the History tab renders from the paginated hook once activated,
  // falling back to the container-fed `executions` prop as a first-page seed
  // before the hook's initial fetch resolves (avoids an empty flash on open).
  const renderHistory = () => {
    const seeded = historyActivated && historyExecutions.length > 0;
    const rows = seeded ? historyExecutions : executions;
    // Show the initial spinner only before we have any rows to display.
    const showInitialLoading = (historyLoading && rows.length === 0) || (loading && !historyActivated);

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-semibold text-neutral-800 dark:text-slate-200">
            Execution History
          </h4>
          <span className="text-sm text-neutral-500 dark:text-slate-400">
            {rows.length}{historyHasMore ? '+' : ''} execution{rows.length === 1 ? '' : 's'}
          </span>
        </div>

        {historyError && (
          <div className="p-3 rounded-md bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 text-sm text-danger-700 dark:text-danger-300">
            {historyError}
          </div>
        )}

        {/* This tab does NOT auto-refetch on a broadcast: it keeps every page the operator has
            loaded, and TanStack refetches an infinite query's pages as a set, so an automatic
            refresh cost ~470 req/s on NP's largest task. It must still never look up-to-date when
            it is not -- so a broadcast for THIS task raises a cue and the operator chooses when to
            pay for the page walk. The Monitor tab is what live watching is for. */}
        {historyHasPendingUpdates && (
          <div
            role="status"
            className="flex items-center justify-between gap-3 p-3 rounded-md bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800"
          >
            <span className="text-sm text-primary-800 dark:text-primary-200">
              New executions available.
            </span>
            <button
              type="button"
              onClick={() => { void refreshHistory(); }}
              disabled={historyLoading || historyRefreshing}
              className="
                px-3 py-1.5 bg-primary-500 hover:bg-primary-600
                text-white border-none rounded text-xs cursor-pointer
                transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {historyRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        )}

        <p className="text-sm text-neutral-600 dark:text-slate-400">
          Select an execution to see its details, including why files were or were not
          collected.
        </p>

        <DataTable
          data={rows}
          columns={executionColumns}
          loading={showInitialLoading}
          striped
          hoverable
          bordered
          compact
          emptyMessage="No execution history available"
          // THUB-278: make a COMPLETED execution's detail reachable. The pickup
          // diagnostics previously rendered only for a live run, which inverts the use
          // case — "why didn't we pick anything up?" is asked AFTER the run finishes.
          onRowSelect={(execution) => setSelectedExecution(execution)}
        />

        {/* Reuses TaskExecutionMonitor rather than forking the rendering logic: a
            duplicated copy is exactly what caused the THUB-186 relative-time mess. */}
        {selectedExecution && (
          <div
            className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-[1100] p-4"
            onClick={() => setSelectedExecution(undefined)}
          >
            <div
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              role="dialog"
              aria-label="Execution detail"
              onClick={(e) => e.stopPropagation()}
            >
              <TaskExecutionMonitor
                task={task}
                execution={selectedExecution}
                onClose={() => setSelectedExecution(undefined)}
              />
            </div>
          </div>
        )}

        {/* Cursor-paginated "Load More" — only when the hook reports another page. */}
        {historyHasMore && (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={() => loadMoreHistory()}
              disabled={historyLoading || historyRefreshing}
              className="
                px-4 py-2 text-sm font-medium rounded-lg
                bg-neutral-100 dark:bg-slate-700
                text-neutral-700 dark:text-slate-200
                hover:bg-neutral-200 dark:hover:bg-slate-600
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              {/* A background refetch says WHY the button is briefly unavailable. Clicking during
                  one would cancel the refetch and discard the fresher page, so it is disabled --
                  but an unexplained disabled button is what made this feel broken before. */}
              {historyLoading ? 'Loading…' : historyRefreshing ? 'Refreshing…' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // THUB-198: Logs renders this task's recent file-journey events (file_audit_api),
  // the sanctioned consumer-observability path — not a CloudWatch stub.
  const renderLogs = () => (
    <div className="space-y-4">
      <h4 className="text-base font-semibold text-neutral-800 dark:text-slate-200">
        Recent File Events
      </h4>
      <p className="text-sm text-neutral-600 dark:text-slate-400">
        Recent file-processing events for this task. Full history is in File History.
      </p>
      {logsLoading && (
        <div className="py-10 text-center text-neutral-500 dark:text-slate-400 italic">
          Loading file events…
        </div>
      )}
      {logsError && (
        <div className="p-3 rounded-md bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 text-sm text-danger-700 dark:text-danger-300">
          {logsError}
        </div>
      )}
      {!logsLoading && !logsError && logEvents && logEvents.length === 0 && (
        <div className="py-10 text-center text-neutral-500 dark:text-slate-400 italic">
          <p>No file events recorded for this task yet.</p>
        </div>
      )}
      {!logsLoading && !logsError && logEvents && logEvents.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 dark:text-slate-400 border-b border-neutral-200 dark:border-slate-700">
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Event</th>
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {logEvents.map((ev) => (
                <tr key={ev.id || ev.eventId || `${ev.fileId}-${ev.timestamp}-${ev.eventType}`}
                    className="border-b border-neutral-100 dark:border-slate-800">
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200 whitespace-nowrap">
                    {formatDate(new Date(ev.timestamp))}
                  </td>
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200">{ev.eventType || '-'}</td>
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200 break-all">{ev.filename}</td>
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200">{ev.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // THUB-183: render the task's SNS topic subscribers (consumer blast-radius).
  const renderSubscribers = () => (
    <div className="space-y-4">
      <h4 className="text-base font-semibold text-neutral-800 dark:text-slate-200">
        SNS Topic Subscribers
      </h4>
      <p className="text-sm text-neutral-600 dark:text-slate-400">
        Value-stream consumers subscribed to this task's notification topic.
      </p>
      {subscribersLoading && (
        <div className="py-10 text-center text-neutral-500 dark:text-slate-400 italic">
          Loading subscribers…
        </div>
      )}
      {subscribersError && (
        <div className="p-3 rounded-md bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 text-sm text-danger-700 dark:text-danger-300">
          {subscribersError}
        </div>
      )}
      {!subscribersLoading && !subscribersError && hasNoTopic && (
        <div className="py-10 text-center text-neutral-500 dark:text-slate-400 italic">
          <p>No SNS topic provisioned for this task yet.</p>
        </div>
      )}
      {!subscribersLoading && !subscribersError && !hasNoTopic && subscribers && subscribers.length === 0 && (
        <div className="py-10 text-center text-neutral-500 dark:text-slate-400 italic">
          <p>No subscribers on this task's topic.</p>
        </div>
      )}
      {!subscribersLoading && !subscribersError && subscribers && subscribers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 dark:text-slate-400 border-b border-neutral-200 dark:border-slate-700">
                <th className="px-3 py-2 font-medium">Protocol</th>
                <th className="px-3 py-2 font-medium">Endpoint</th>
                <th className="px-3 py-2 font-medium">Owner Account</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub) => (
                <tr key={sub.subscriptionArn || `${sub.protocol}-${sub.endpoint}`}
                    className="border-b border-neutral-100 dark:border-slate-800">
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200">{sub.protocol}</td>
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200 break-all">{sub.endpoint}</td>
                  <td className="px-3 py-2 text-neutral-800 dark:text-slate-200">{sub.ownerAccountId}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      sub.status === 'confirmed'
                        ? 'bg-success-100 dark:bg-success-900/40 text-success-700 dark:text-success-300'
                        : 'bg-warning-100 dark:bg-warning-900/40 text-warning-700 dark:text-warning-300'
                    }`}>
                      {sub.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className={`
      bg-white dark:bg-slate-900
      rounded-lg overflow-hidden
      shadow-lg flex flex-col h-full
      ${className}
    `}>
      {/* Version Conflict Toast - temporarily disabled for debugging */}
          {/* THUB-298: the commented-out VersionConflictToast / EntityUpdateIndicator block that sat
              here is gone, along with the components themselves. The feature could not be re-enabled
              by uncommenting: its driver hook (hooks/useVersionConflict) was already deleted as
              unreachable, so the components were orphans that still shipped in the bundle via the
              shared barrel. Git history has the original if version-conflict detection is revived. */}

      {/* Header */}
      <div className="
        flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4
        px-6 py-5 flex-shrink-0
        bg-neutral-50 dark:bg-slate-800/50
        border-b border-neutral-200 dark:border-slate-700
      ">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-slate-100">
            {task.name}
          </h2>
          <StatusIndicator
            status={getStatusColor(task.status)}
            label={task.status.charAt(0).toUpperCase() + task.status.slice(1)}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RoleGuard minimumRole="operator">
            <button
              onClick={() => handleAction('toggle')}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg
                transition-colors duration-150
                ${task.status === 'enabled'
                  ? 'bg-danger-500 hover:bg-danger-600 text-white'
                  : 'bg-neutral-100 dark:bg-slate-700 text-neutral-700 dark:text-slate-200 hover:bg-neutral-200 dark:hover:bg-slate-600'
                }
              `}
            >
              {task.status === 'enabled' ? 'Disable' : 'Enable'}
            </button>
          </RoleGuard>
          {canExecuteNow && (
            <RoleGuard minimumRole="operator">
              <button
                onClick={() => handleAction('execute')}
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-200
                  hover:bg-neutral-200 dark:hover:bg-slate-600
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
                disabled={task.lastExecution?.status === 'running'}
              >
                Execute Now
              </button>
            </RoleGuard>
          )}
          <RoleGuard minimumRole="admin">
            <button
              onClick={() => handleAction('edit')}
              className="
                px-4 py-2 text-sm font-medium rounded-lg
                bg-neutral-100 dark:bg-slate-700
                text-neutral-700 dark:text-slate-200
                hover:bg-neutral-200 dark:hover:bg-slate-600
                transition-colors duration-150
              "
            >
              Edit
            </button>
          </RoleGuard>
          <RoleGuard minimumRole="admin">
            <button
              onClick={() => handleAction('delete')}
              className="
                px-4 py-2 text-sm font-medium rounded-lg
                bg-danger-500 hover:bg-danger-600
                text-white
                transition-colors duration-150
              "
            >
              Delete
            </button>
          </RoleGuard>
          {onClose && (
            <button
              onClick={onClose}
              className="
                w-10 h-10 flex items-center justify-center
                text-2xl text-neutral-500 dark:text-slate-400
                hover:text-neutral-700 dark:hover:text-slate-200
                hover:bg-neutral-200 dark:hover:bg-slate-700
                rounded-lg transition-colors duration-150
              "
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="
        flex flex-shrink-0 border-b border-neutral-200 dark:border-slate-700
        bg-neutral-50 dark:bg-slate-800/30
        overflow-x-auto
      ">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => selectTab(tab.id)}
            className={`
              px-5 py-3 text-sm font-medium border-b-2 transition-all duration-150 whitespace-nowrap
              ${activeTab === tab.id
                ? 'text-primary-600 dark:text-primary-400 border-primary-500 bg-white dark:bg-slate-900'
                : 'text-neutral-600 dark:text-slate-400 border-transparent hover:text-neutral-900 dark:hover:text-slate-200 hover:bg-neutral-100 dark:hover:bg-slate-800'
              }
            `}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`
                ml-2 px-1.5 py-0.5 text-xs rounded-full
                ${activeTab === tab.id
                  ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300'
                  : 'bg-neutral-100 dark:bg-slate-700 text-neutral-600 dark:text-slate-400'
                }
              `}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6 bg-white dark:bg-slate-900 flex-1 overflow-y-auto min-h-0">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'configuration' && renderConfiguration()}
        {activeTab === 'history' && renderHistory()}
        {activeTab === 'logs' && renderLogs()}
        {activeTab === 'monitor' && (
          // THUB-278: seed the Monitor with the task's most recent execution.
          //
          // This mount never passed `execution`, and TaskExecutionMonitor only
          // self-hydrates for a pending/running `task.lastExecution`. So for any task
          // whose latest run had FINISHED, the Monitor rendered "No active execution"
          // — and because the THUB-226 pickup diagnostics live inside that same
          // `execution ? ... :` branch, they could never be reached on a completed
          // pickup. That is the whole reason the diagnostics appeared to be missing:
          // the data was in the API response all along, the panel holding it was
          // simply never rendered.
          //
          // `executions` is newest-first (the API queries the task GSI with
          // ScanIndexForward=False), so [0] is the latest run.
          <TaskExecutionMonitor
            task={task}
            execution={monitorSeedExecution}
          />
        )}
        {activeTab === 'subscribers' && renderSubscribers()}
        {activeTab === 'audit' && (
          <AuditLogViewer
            entityType="task"
            entityId={task.taskId}
            title="Task Change History"
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-[1100] p-4"
          onClick={() => !isDeletingProp && setShowDeleteConfirm(false)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-lg shadow-xl
              w-full max-w-md overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-800">
              <h3 className="text-lg font-semibold text-danger-600 dark:text-danger-400">
                Delete Task
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-neutral-900 dark:text-slate-100">
                Are you sure you want to delete task <strong>{task.name}</strong>?
              </p>
              <p className="text-sm text-neutral-600 dark:text-slate-400">
                This action cannot be undone. Any associated AWS resources (SNS topics, EventBridge schedules,
                S3 event notifications) will be cleaned up.
              </p>
              {deleteErrorProp && (
                <div className="p-3 rounded-md bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 text-sm text-danger-700 dark:text-danger-300">
                  {deleteErrorProp}
                </div>
              )}
            </div>
            <div className="
              flex justify-end gap-3 px-5 py-4
              border-t border-neutral-200 dark:border-slate-700
              bg-neutral-50 dark:bg-slate-800
            ">
              <button
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-200
                  hover:bg-neutral-200 dark:hover:bg-slate-600
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingProp}
              >
                Cancel
              </button>
              <button
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-danger-500 hover:bg-danger-600
                  text-white
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
                onClick={() => {
                  onDelete?.(task.taskId);
                  setShowDeleteConfirm(false);
                }}
                disabled={isDeletingProp}
              >
                {isDeletingProp ? 'Deleting...' : 'Delete Task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
